import {
  getSystemOverviewOptions as getCuSystemOverviewOptions,
  startControlUnitMutation,
  stopControlUnitMutation,
} from '@/api/cuSystemAnalyzerApi'
import { Client as CuClient } from '@/api/cuSystemAnalyzerApi/client'
import {
  getSystemOverviewOptions,
  startCitysenseServicesMutation,
  stopCitysenseServicesMutation,
} from '@/api/systemAnalyzerApi'
import { Client } from '@/api/systemAnalyzerApi/client'
import { ServiceActionResult, SystemOverview } from '@/api/systemAnalyzerApi/types.gen'
import ErrorState from '@/components/Common/ErrorState'
import { DIAGNOSTICS_SYSTEM_OVERVIEW_REFETCH_INTERVAL_MS } from '@/constants'
import { extendKey } from '@/utils/queryKey'
import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  List,
  LoadingOverlay,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { QueryFunction, QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlay, FaStop, FaSync } from 'react-icons/fa'
import { TbAlertTriangle, TbCircleMinus, TbCircleX, TbInfoCircle } from 'react-icons/tb'
import { DiagnosticsSystem } from './types'
import { useDiagnosticsQuery } from './useDiagnosticsQuery'
import { useDiagnosticsTarget } from './useDiagnosticsTarget'
import { isServiceActionResult, isSystemOverview } from './validation'

const STATUS_COLORS: Record<SystemOverview['status'], string> = {
  ok: 'green',
  degraded: 'yellow',
  error: 'red',
}

const SERVICE_LABELS = {
  cs: {
    title: 'serviceControl',
    start: 'startServices',
    stop: 'stopServices',
    confirmStart: 'confirmStartServices',
    confirmStop: 'confirmStopServices',
    started: 'servicesStarted',
    stopped: 'servicesStopped',
    startFailed: 'startServicesFailed',
    stopFailed: 'stopServicesFailed',
  },
  cu: {
    title: 'cuServiceControl',
    start: 'startCuService',
    stop: 'stopCuService',
    confirmStart: 'confirmStartCuService',
    confirmStop: 'confirmStopCuService',
    started: 'cuServiceStarted',
    stopped: 'cuServiceStopped',
    startFailed: 'startCuServiceFailed',
    stopFailed: 'stopCuServiceFailed',
  },
} as const

const OVERVIEW_TITLES = {
  cs: 'csSystemOverview',
  cu: 'cuSystemOverview',
} as const

type OverviewOptions = { queryFn?: QueryFunction<unknown, QueryKey>; queryKey: QueryKey }

type ServiceActionMutation = {
  mutationFn?: (variables: object) => Promise<ServiceActionResult>
}

const PredicateList = ({
  title,
  color,
  icon,
  messages,
}: {
  title: string
  color: string
  icon: ReactNode
  messages: string[]
}) => {
  if (messages.length === 0) return null

  return (
    <Stack gap="xs">
      <Text fw={500} c={color}>
        {title}
      </Text>
      <List
        size="sm"
        spacing="xs"
        center
        icon={
          <ThemeIcon color={color} variant="light" size={22} radius="xl">
            {icon}
          </ThemeIcon>
        }
      >
        {messages.map(message => (
          <List.Item key={message}>{message}</List.Item>
        ))}
      </List>
    </Stack>
  )
}

const ServiceControl = ({
  system,
  source,
  startMutation,
  stopMutation,
  onSettled,
}: {
  system: DiagnosticsSystem
  source: string
  startMutation: ServiceActionMutation
  stopMutation: ServiceActionMutation
  onSettled: () => void
}) => {
  const { t } = useTranslation(['diagnostics', 'common'])
  const labels = SERVICE_LABELS[system]

  const notifyResult = (result: ServiceActionResult | undefined, successMessage: string, failureMessage: string) => {
    if (!isServiceActionResult(result)) {
      notifications.show({ title: failureMessage, message: t('unexpectedResponse'), color: 'red' })
      return
    }
    if (result.status === 'error') {
      notifications.show({ title: failureMessage, message: result.message, color: 'red' })
      return
    }
    notifications.show({ title: successMessage, message: result.message, color: 'green' })
  }

  const { mutate: start, isPending: isStarting } = useMutation({
    ...startMutation,
    onSuccess: result => {
      notifyResult(result, t(labels.started), t(labels.startFailed))
      onSettled()
    },
    onError: () => notifications.show({ title: t(labels.startFailed), message: '', color: 'red' }),
  })

  const { mutate: stop, isPending: isStopping } = useMutation({
    ...stopMutation,
    onSuccess: result => {
      notifyResult(result, t(labels.stopped), t(labels.stopFailed))
      onSettled()
    },
    onError: () => notifications.show({ title: t(labels.stopFailed), message: '', color: 'red' }),
  })

  const confirmStart = () =>
    modals.openConfirmModal({
      title: t(labels.start),
      children: <Text size="sm">{t(labels.confirmStart, { source: source.toUpperCase() })}</Text>,
      labels: { confirm: t(labels.start), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'green' },
      onConfirm: () => start({}),
    })

  const confirmStop = () =>
    modals.openConfirmModal({
      title: t(labels.stop),
      children: <Text size="sm">{t(labels.confirmStop, { source: source.toUpperCase() })}</Text>,
      labels: { confirm: t(labels.stop), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'red' },
      onConfirm: () => stop({}),
    })

  return (
    <Group justify="space-between" align="center">
      <Text fw={500}>{t(labels.title)}</Text>
      <Group>
        <Button
          leftSection={<FaPlay size={12} />}
          color="green"
          onClick={confirmStart}
          loading={isStarting}
          disabled={isStopping}
        >
          {t(labels.start)}
        </Button>
        <Button
          leftSection={<FaStop size={12} />}
          color="red"
          onClick={confirmStop}
          loading={isStopping}
          disabled={isStarting}
        >
          {t(labels.stop)}
        </Button>
      </Group>
    </Group>
  )
}

const SystemOverviewCard = ({
  system,
  source,
  overviewOptions,
}: {
  system: DiagnosticsSystem
  source: string
  overviewOptions: OverviewOptions
}) => {
  const { t } = useTranslation(['diagnostics', 'common'])
  const target = useDiagnosticsTarget(system, source)

  const { data, isLoading, isFetching, isError, refetch } = useDiagnosticsQuery({
    ...overviewOptions,
    refetchInterval: DIAGNOSTICS_SYSTEM_OVERVIEW_REFETCH_INTERVAL_MS,
  })

  const overview = isSystemOverview(data) ? data : undefined
  const hasInvalidResponse = !isError && data !== undefined && !overview
  const counts = overview?.counts

  return (
    <Card shadow="sm" p="lg" radius="md" withBorder pos="relative" data-testid={`${system}-system-overview`}>
      <LoadingOverlay visible={isLoading} />
      <Group justify="space-between" align="center" mb="md">
        <Group align="center">
          <Text fw={500}>{t(OVERVIEW_TITLES[system])}</Text>
          {overview && (
            <Badge color={STATUS_COLORS[overview.status]} variant="light">
              {t(`status_${overview.status}`)}
            </Badge>
          )}
        </Group>
        <Button
          variant="light"
          size="xs"
          leftSection={<FaSync size={12} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          {t('refresh')}
        </Button>
      </Group>

      {isError && (
        <ErrorState
          title={t('overviewFetchFailed')}
          message={t('overviewFetchFailedMessage', { source: target })}
          showBackButton={false}
          width="100%"
        />
      )}

      {hasInvalidResponse && (
        <ErrorState
          title={t('unexpectedResponse')}
          message={t('overviewUnexpectedResponseMessage', { source: target })}
          showBackButton={false}
          width="100%"
        />
      )}

      {counts && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} mb="md">
          <Badge color="green" variant="light" size="lg">
            {t('countOk')}: {counts.ok}
          </Badge>
          <Badge color="red" variant="light" size="lg">
            {t('countError')}: {counts.error}
          </Badge>
          <Badge color="yellow" variant="light" size="lg">
            {t('countWarning')}: {counts.warning}
          </Badge>
          <Badge color="blue" variant="light" size="lg">
            {t('countInfo')}: {counts.info}
          </Badge>
          <Badge color="gray" variant="light" size="lg">
            {t('countSkipped')}: {counts.skipped}
          </Badge>
          <Badge color="dark" variant="light" size="lg">
            {t('countTotal')}: {counts.total}
          </Badge>
        </SimpleGrid>
      )}

      {overview && (
        <Stack gap="md">
          <PredicateList
            title={t('errorPredicates')}
            color="red"
            icon={<TbCircleX size={14} />}
            messages={overview.error_predicates}
          />
          <PredicateList
            title={t('warningPredicates')}
            color="yellow"
            icon={<TbAlertTriangle size={14} />}
            messages={overview.warning_predicates}
          />
          <PredicateList
            title={t('infoPredicates')}
            color="blue"
            icon={<TbInfoCircle size={14} />}
            messages={overview.info_predicates}
          />
          <PredicateList
            title={t('skippedPredicates')}
            color="gray"
            icon={<TbCircleMinus size={14} />}
            messages={overview.skipped_predicates.map(
              predicate => `${predicate.name} (${t('dependsOn', { dependencies: predicate.depends_on.join(', ') })})`
            )}
          />
          {overview.error_predicates.length === 0 &&
            overview.warning_predicates.length === 0 &&
            overview.info_predicates.length === 0 &&
            overview.skipped_predicates.length === 0 && <Text size="sm">{t('allPredicatesPassed')}</Text>}
        </Stack>
      )}
    </Card>
  )
}

const SystemOverviewPanel = ({ client, cuClient, source }: { client: Client; cuClient: CuClient; source: string }) => {
  const queryClient = useQueryClient()

  // Both analyzers are reached through the same vehicle proxy URL, so the generated query keys only differ
  // by the system tag appended here
  const csOverviewOptions = extendKey(getSystemOverviewOptions({ client }), source, 'cs')
  const cuOverviewOptions = extendKey(getCuSystemOverviewOptions({ client: cuClient }), source, 'cu')

  return (
    <Tabs.Panel value="overview">
      <Stack gap="md" mt="lg">
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <ServiceControl
              system="cs"
              source={source}
              startMutation={startCitysenseServicesMutation({ client })}
              stopMutation={stopCitysenseServicesMutation({ client })}
              onSettled={() => queryClient.invalidateQueries({ queryKey: csOverviewOptions.queryKey })}
            />
            <Divider />
            <ServiceControl
              system="cu"
              source={source}
              startMutation={startControlUnitMutation({ client: cuClient })}
              stopMutation={stopControlUnitMutation({ client: cuClient })}
              onSettled={() => queryClient.invalidateQueries({ queryKey: cuOverviewOptions.queryKey })}
            />
          </Stack>
        </Card>

        <SimpleGrid cols={{ base: 1, lg: 2 }}>
          <SystemOverviewCard system="cs" source={source} overviewOptions={csOverviewOptions} />
          <SystemOverviewCard system="cu" source={source} overviewOptions={cuOverviewOptions} />
        </SimpleGrid>
      </Stack>
    </Tabs.Panel>
  )
}

export default SystemOverviewPanel
